import {
	AfterViewInit,
	Component,
	EventEmitter,
	forwardRef,
	Input,
	OnChanges,
	OnDestroy,
	OnInit,
	Output,
	SimpleChanges,
	ViewChild
} from '@angular/core';
import { NgModel, NG_VALIDATORS, ValidationErrors, AbstractControl } from '@angular/forms';
import { BehaviorSubject, Observable, combineLatest, of, Observer, from } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';

import { ILiveSubtitles } from 'rev-shared/ui/liveSubtitles/ILiveSubtitles';
import { IMediaFeatures } from 'rev-shared/media/IMediaFeatures';
import { RevIQService } from 'rev-shared/revIQ/RevIQ.Service';
import { TranscriptionLanguagesService } from 'rev-shared/media/transcription/TranscriptionLanguages.Service';
import { UserContextService } from 'rev-shared/security/UserContext.Service';
import { DTMF_CODES_REGEX, toggleError } from 'rev-shared/util/directives/form/validation/validationUtil';
import { getInitialDtmfCodeFn } from 'rev-shared/media/DtmfUtils';

import { ISearchLoader } from 'rev-portal/search/Contract';
import { RecordingService } from 'rev-portal/media/recording/Recording.Service';
import { VideoSearchService } from 'rev-portal/search/VideoSearch.Service';
import { getAvailableWebcastSources, WebcastVideoSource } from 'rev-portal/scheduledEvents/webcast/WebcastVideoSource';

import { EditWebcastBaseSettingsComponent } from './EditWebcastBaseSettings.Component';
import { EditWebcastModel } from './EditWebcastModel';
import { IRtmpSettings } from './IRtmpSettings';

import {
	LIVE_SUBTITLES_ERRORS,
	MicrosoftTeamsTenantIdRequired,
	RtmpListenerUrlConflict,
	SecondaryRtmpListenerUrlConflict,
	UnsupportedLiveSubtitlesPresentationProfile
} from './EditWebcast.Service';

import { IPresentationProfile } from './EditWebcast.Contract';
import styles from './EditWebcast.module.less';

const TypeaheadResultSize = 25;

@Component({
	selector: 'edit-webcast-video-settings',
	templateUrl: './EditWebcastVideoSettings.Component.html',
	providers: [{
		provide: NG_VALIDATORS,
		useExisting: forwardRef(() => EditWebcastVideoSettingsComponent),
		multi: true,
	}],
	standalone: false
})
export class EditWebcastVideoSettingsComponent extends EditWebcastBaseSettingsComponent implements AfterViewInit, OnChanges, OnInit, OnDestroy {
	@Input() public webcast: EditWebcastModel;
	@Input() public features: IMediaFeatures;
	@Input() public externalErrors: ValidationErrors;
	@Input() public startDate: Date;

	@Output() public automatedWebcastValidationChanged: EventEmitter<boolean> = new EventEmitter();
	@Output() public videoSourceChanged: EventEmitter<void> = new EventEmitter();

	@ViewChild('ewVideoSourceSipAddress')
	public ewVideoSourceSipAddress: NgModel;

	public enableLiveSubtitles$: Observable<boolean>;
	public subtitleIssue: string;
	public rtmpVideoSourceIssue: string;
	public showVodDurationError: boolean;
	public vodErrorInterval: number;
	public allowLiveSubtitles: boolean;
	public availableSources: Array<{ label: string, value: string }>;
	public sipAddresses$: Observable<any>;
	public videoSearchLoader: ISearchLoader;

	public readonly enableVodWebcast = true;
	public readonly WebcastVideoSource = WebcastVideoSource;
	public readonly styles = styles;
	public readonly DTMF_CODES_REGEX = DTMF_CODES_REGEX;
	private readonly getInitialDtmfCode = getInitialDtmfCodeFn();

	private liveSubtitles$: BehaviorSubject<ILiveSubtitles>;
	private webcastVideoSource$: BehaviorSubject<WebcastVideoSource>;
	private presentationProfile$: BehaviorSubject<IPresentationProfile>;

	constructor(
		protected readonly TranslateService: TranslateService,
		protected readonly TranscriptionLanguagesService: TranscriptionLanguagesService,
		private readonly VideoSearchService: VideoSearchService,
		private readonly RecordingService: RecordingService,
		private readonly UserContext: UserContextService,
		private readonly RevIQService: RevIQService
	) {
		super();
	}

	public ngOnInit(): void {
		this.liveSubtitles$ = new BehaviorSubject<ILiveSubtitles>(this.webcast.liveSubtitles);
		this.webcastVideoSource$ = new BehaviorSubject<WebcastVideoSource>(this.webcast.videoSource);
		this.presentationProfile$ = new BehaviorSubject<IPresentationProfile>(undefined);
		this.videoSearchLoader = this.VideoSearchService.getVideoSearchLoader(this.UserContext.getAccount().id);
		this.TranscriptionLanguagesService.loadLiveTranscriptionLanguages();
		this.TranscriptionLanguagesService.loadAiServiceTranslationLanguages();
		this.initSubtitlesObservables();
		this.vodErrorInterval = window.setInterval(this.updateVodErrors.bind(this), 2000);
		this.updateVodErrors();

		//handling webcast sources
		this.availableSources = getAvailableWebcastSources(this.TranslateService, this.features);
		this.sipAddresses$ = this.getRecentCalls$();
	}

	public ngOnChanges(changes: SimpleChanges): void {
		if(changes.startDate){
			this.updateVodErrors();
			this.validateAutomatedWebcast();
		}

		if (changes.externalErrors) {
			this.validateLiveSubtitles();
			this.validateRtmpVideoSource();
			this.validateMicrosoftTeamsSource();
		}
	}

	public ngAfterViewInit(): void {
		super.ngAfterViewInit();
		setTimeout(() => {
			this.validateVideoSourceOption();
			this.validateVodVideoSource();
			this.markPresentationProfileToDirty();
		});
	}

	public ngOnDestroy(): void {
		super.ngOnDestroy();
		window.clearInterval(this.vodErrorInterval);
	}

	public validate(): ValidationErrors {
		const errors = super.validate();
		if (errors) {
			if (this.form?.control?.get('presentationProfile')?.errors?.presentationProfileConflict) {
				return { presentationProfileConflict: true };
			}
			return errors;
		}
	}

	public get enableWebexLiveStream(): boolean {
		return this.features?.webexSitesSettings?.some(site => site.enableWebexLiveStream);
	}

	public get showAutomatedWebcast(): boolean {
		return this.webcast.videoSource !== WebcastVideoSource.WEBRTC_SINGLE_PRESENTER &&
			this.webcast.videoSource !== WebcastVideoSource.PRODUCER;
	}

	public validateAutomatedWebcast(): void {
		const errors = this.externalErrors?.automatedDateInPast;
		const valid = !this.webcast.automatedWebcast ||
			!this.showAutomatedWebcast ||
			this.webcast.startDate?.getTime() >= Date.now();

		const control = this.getControl('automatedWebcastTrue');
		if(control) {
			toggleError(control, 'automatedDateInPast', !valid);
		}

		if(errors && valid) {
			this.automatedWebcastValidationChanged.emit(true);
		}
	}

	public updateVideoSource(): void {

		if (this.webcast.videoSource !== WebcastVideoSource.RTMP) {
			this.onRtmpSettingsChange(undefined);
		}

		if (this.webcast.videoSource === WebcastVideoSource.VOD) {
			this.webcast.disableAutoRecording = true;
			this.webcast.autoAssociateVod = false;
			this.webcast.redirectVod = false;
			this.webcast.presentationFile = undefined;
			this.updateLiveSubtitles({
				isLiveSubtitlesEnabled: false
			});
		}
		this.updateDtmfInitialCodes();
		this.validateVideoSourceOption();
		this.webcastVideoSource$.next(this.webcast.videoSource);
		this.validateAutomatedWebcast();
		this.videoSourceChanged.emit();
	}

	public updateLiveSubtitles($event: ILiveSubtitles): void {
		this.webcast.liveSubtitles = $event;
		this.liveSubtitles$.next(this.webcast.liveSubtitles);
	}

	public updateDtmfInitialCodes(): void {
		this.webcast.vcDtmf = this.getInitialDtmfCode(this.webcast.videoSource);
	}

	public validateVideoSettingsForm(errorFlags: any, errorType: string): void {
		const control = this.form.control.get('videoSettingsFormHidden');
		control.markAsDirty();
		toggleError(control, errorType, !!errorFlags);
	}

	public onPresentationProfileChange(profile: IPresentationProfile): void {
		this.presentationProfile$.next(profile);
	}

	public onRtmpSettingsChange(rtmpSettings: IRtmpSettings): void {
		this.webcast.rtmpSettings = rtmpSettings;
	}

	private initSubtitlesObservables(): void {
		this.enableLiveSubtitles$ = combineLatest([
			from(this.RevIQService.isRevIQEligibleAndAuthorized(this.UserContext.getAccount().id)),
			this.liveSubtitles$, this.webcastVideoSource$, this.presentationProfile$])
			.pipe(
				map(([revIQEnabled, liveSubtitles, webcastVideoSource, profile]) =>
					revIQEnabled &&
					(!!liveSubtitles?.sourceLanguage || this.validSubtitleSource(webcastVideoSource, profile))
				),
				tap(() => this.validateLiveSubtitles())
			);
	}

	private markPresentationProfileToDirty(): void {
		const control = this.getControl('presentationProfile');
		if (this.webcast && this.webcast.cloneWebcastId && control) {
			control.markAsDirty();
		}
	}

	private validateLiveSubtitles(): void {
		this.subtitleIssue = !this.webcast.liveSubtitles
			? undefined
			: this.invalidSubtitlesPresentationProfile()
				? UnsupportedLiveSubtitlesPresentationProfile
				: LIVE_SUBTITLES_ERRORS.find(error => {
					if(this.externalErrors?.[error]) {
						return error;
					}
				});
	}

	private validateRtmpVideoSource(): void {
		this.rtmpVideoSourceIssue = this.externalErrors?.[RtmpListenerUrlConflict] ? RtmpListenerUrlConflict :
			this.externalErrors?.[SecondaryRtmpListenerUrlConflict] ? SecondaryRtmpListenerUrlConflict : undefined;
	}

	private validSubtitleSource(source: WebcastVideoSource, profile: IPresentationProfile): boolean {
		return (source !== WebcastVideoSource.PRESENTATION || this.isEnrichedPresentationProfile(profile))
			&& source !== WebcastVideoSource.VOD;
	}

	private invalidSubtitlesPresentationProfile(): boolean {
		return this.webcast.videoSource === WebcastVideoSource.PRESENTATION
			&& this.presentationProfile$?.value && !this.isEnrichedPresentationProfile(this.presentationProfile$.value);
	}

	private isEnrichedPresentationProfile(profile: IPresentationProfile): boolean {
		return profile?.isLiveEnrichable;
	}

	private getRecentCalls$(): Observable<string[]> {
		// Typeahead can accept an observable as input
		// Map http repsonse to a filtered list based on what was being inputted
		return new Observable((observer: Observer<string>) => {
			observer.next(this.webcast.vcSipAddress);
		}).pipe(
			switchMap((query: string) => {
				if (query) {
					return this.RecordingService.recentVideoConferenceCalls().pipe(
						map((data: any) => {
							return data?.recentVcCalls
								.filter(entry => entry.sipAddress.includes(query))
								.map(entry => entry.sipAddress) ?? [];
						}),
						catchError(err => {
							console.error(err);
							return of([]);
						})
					);
				}
				return of([]);
			})
		);
	}

	public updateVodErrors(): void {
		if(this.webcast.videoSource !== WebcastVideoSource.VOD || !this.webcast.vodInfo) {
			this.showVodDurationError = false;
			return;
		}
		this.markVodSourceControl();
		const a = this.webcast.startDate?.getTime();
		const b = this.webcast.endDate?.getTime();
		const t = Date.now();

		this.showVodDurationError = (b - a) < this.webcast.vodInfo.durationMs;

	}

	private validateVodVideoSource(): void {
		if (this.webcast.id &&
			this.webcast.videoSource === WebcastVideoSource.VOD &&
			!this.webcast.vodInfo
		) {
			this.markVodSourceControl();
		}
	}

	private markVodSourceControl(): void {
		const control = this.getControl('vodVideoSource');
		if (control) {
			control.markAsTouched();
			control.markAsDirty();
		}
	}

	private validateVideoSourceOption(): void {
		const isAvailble = this.availableSources.some(source => source.value === this.webcast.videoSource);
		const control = this.getControl('videoSourceOptions');
		if (!isAvailble || control.invalid) {
			toggleError(control, 'sourceOptionNotAvailable', !isAvailble);
			control.markAsDirty();
			control.markAsTouched();
		}
	}
	private validateMicrosoftTeamsSource(): void {
		const hasError = this.externalErrors?.[MicrosoftTeamsTenantIdRequired];
		const control = this.getControl('videoSourceMSTeams');
		if (control) {
			toggleError(control, 'microsoftTeamsTenantIdRequired', !!hasError);
			control.markAsDirty();
			control.markAsTouched();
		}
	}

	private getControl(name: string): AbstractControl {
		return this.form?.control?.get(name);
	}
}
